import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import { IMasterCurricula } from 'infrastructure/api'
import { migrateSchoolCurriculaToDistrict } from 'shared/api/mutations'
import { useMastersByIdWithErrorNotification } from 'shared/api/masters'

interface ICurriculumsAddedOnSchoolLevel {
  name: string
  school_count: number
}

const filterMasterCurricula = (
  schoolLevelCurriculum: Array<ICurriculumsAddedOnSchoolLevel>,
  masterTrees:
    | {
        [title: string]: IMasterCurricula
      }
    | undefined,
) => {
  if (!masterTrees) return schoolLevelCurriculum
  const masterTitles = Object.keys(masterTrees)
  return schoolLevelCurriculum.filter(({ name }) => masterTitles.includes(name))
}

const groupByTitle = (masterTrees: {
  [treeId: string]: IMasterCurricula[]
}): {
  [title: string]: IMasterCurricula
} =>
  Object.values(masterTrees).reduce(
    (acc: { [title: string]: IMasterCurricula }, items) => {
      const filterDemos = items.filter(
        ({ name, title }) =>
          !name.toLowerCase().includes('demo') &&
          !title.toLowerCase().includes('demo'),
      )
      if (!filterDemos.length) return acc
      const sortedItems = items.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      if (!acc[sortedItems[0].title]) {
        return {
          ...acc,
          [sortedItems[0].title]: sortedItems[0],
        }
      }
      // if existing one is recent one
      const existingVersionDate = new Date(acc[sortedItems[0].title].updatedAt)
      const filterOneDate = new Date(sortedItems[0].updatedAt)
      if (filterOneDate > existingVersionDate)
        return {
          ...acc,
          [sortedItems[0].title]: sortedItems[0],
        }
      return acc
    },
    {},
  )

export default function MigrateCurriculaToDistrict({
  district,
  totalSchoolCount,
  setHasCurriculumToMigrate,
}: {
  district: {
    id: string
    curriculumsAddedOnSchoolLevel: Array<ICurriculumsAddedOnSchoolLevel>
  }
  totalSchoolCount: number
  setHasCurriculumToMigrate: (val: boolean) => void
}) {
  const { id, curriculumsAddedOnSchoolLevel } = district

  const mastersById = useMastersByIdWithErrorNotification()

  const [masters, setMasters] = useState<{
    [title: string]: IMasterCurricula
  }>({})
  useEffect(() => {
    if (!mastersById) return
    setMasters(groupByTitle(mastersById))
  }, [mastersById])

  const masterSchoolCurriculum = filterMasterCurricula(
    curriculumsAddedOnSchoolLevel,
    masters,
  )
  const [migratingCurriculum, setMigratingCurriculum] = useState<Array<string>>(
    [],
  )
  const [successFullyMigrated, setSuccessfullyMigrated] = useState<
    Array<string>
  >([])

  useEffect(() => {
    if (masterSchoolCurriculum.length) {
      setHasCurriculumToMigrate(true)
    }
  }, [masterSchoolCurriculum.length, setHasCurriculumToMigrate])

  const migrateCurriculaToDistrict = useCallback(
    async (title: string) => {
      const treeDetails = masters[title]
      if (!treeDetails) return
      setMigratingCurriculum((curriculum) => [...curriculum, title])
      await migrateSchoolCurriculaToDistrict({
        districtId: +id,
        masterTreeId: treeDetails.treeId,
        masterVersion: treeDetails.version,
      })
      setMigratingCurriculum(migratingCurriculum.filter((val) => val !== title))
      setSuccessfullyMigrated((curriculum) => [...curriculum, title])
    },
    [masters, id, migratingCurriculum, setSuccessfullyMigrated],
  )

  if (!masterSchoolCurriculum.length) return <b>No Curriculum to Migrate</b>

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>
            <b>Curricula Name</b>
          </TableCell>
          <TableCell>
            <b>Number of School using</b>
          </TableCell>
          <TableCell>
            <b>Version Name</b>
          </TableCell>
          <TableCell>
            <b>Action</b>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {masterSchoolCurriculum.map(({ name, school_count }) => (
          <TableRow key={name}>
            <TableCell component="th" scope="row">
              {name}
            </TableCell>
            <TableCell component="th" scope="row">
              {`${school_count} / ${totalSchoolCount}`}
            </TableCell>
            <TableCell component="th" scope="row">
              {masters[name].name}
            </TableCell>
            <TableCell component="th" scope="row">
              {migratingCurriculum.includes(name) && 'Migrating...'}
              {successFullyMigrated.includes(name) && 'Migrated'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
